import { useState, useEffect } from 'react';
import { Layout } from '../components/Layout';
import { projectsAPI } from '../lib/api';
import { 
    Plus, 
    Pencil, 
    Trash2, 
    FolderKanban,
    Hash
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
} from '../components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';

export default function Projects() {
    const [projects, setProjects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [formData, setFormData] = useState({ name: '', billing_number: '' });
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        fetchProjects();
    }, []);

    const fetchProjects = async () => {
        try {
            const response = await projectsAPI.getAll();
            setProjects(response.data);
        } catch (error) {
            toast.error('Failed to load projects');
        } finally {
            setLoading(false);
        }
    };

    const openCreateDialog = () => {
        setSelectedProject(null);
        setFormData({ name: '', billing_number: '' });
        setDialogOpen(true);
    };

    const openEditDialog = (project) => {
        setSelectedProject(project);
        setFormData({ name: project.name, billing_number: project.billing_number });
        setDialogOpen(true);
    };

    const openDeleteDialog = (project) => {
        setSelectedProject(project);
        setDeleteDialogOpen(true);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSaving(true);

        try {
            if (selectedProject) {
                await projectsAPI.update(selectedProject.id, formData);
                toast.success('Project updated');
            } else {
                await projectsAPI.create(formData);
                toast.success('Project created');
            }
            setDialogOpen(false);
            fetchProjects();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to save project');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        try {
            await projectsAPI.delete(selectedProject.id);
            toast.success('Project deleted');
            setDeleteDialogOpen(false);
            fetchProjects();
        } catch (error) {
            toast.error('Failed to delete project');
        }
    };

    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-64">
                    <div className="text-zinc-500">Loading...</div>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="space-y-8" data-testid="projects-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 animate-fade-in">
                    <div>
                        <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            Projects
                        </h1>
                        <p className="text-lg text-zinc-500">
                            Manage your projects and billing numbers
                        </p>
                    </div>
                    <Button 
                        onClick={openCreateDialog} 
                        className="swiss-btn-primary"
                        data-testid="create-project-btn"
                    >
                        <Plus className="w-4 h-4 mr-2" />
                        New Project
                    </Button>
                </div>

                {/* Projects Grid */}
                {projects.length === 0 ? (
                    <div className="swiss-card animate-fade-in">
                        <div className="empty-state">
                            <FolderKanban className="empty-state-icon" />
                            <h3 className="text-xl font-semibold text-zinc-900 mb-2">No projects yet</h3>
                            <p className="text-zinc-500 mb-4">Create your first project to start tracking time</p>
                            <Button onClick={openCreateDialog} className="swiss-btn-primary" data-testid="create-first-project-btn">
                                <Plus className="w-4 h-4 mr-2" />
                                Create Project
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map((project, index) => (
                            <div 
                                key={project.id} 
                                className="swiss-card animate-fade-in"
                                style={{ animationDelay: `${index * 0.05}s` }}
                                data-testid={`project-card-${project.id}`}
                            >
                                <div className="flex items-start justify-between mb-4">
                                    <div className="w-12 h-12 bg-orange-100 rounded-sm flex items-center justify-center">
                                        <FolderKanban className="w-6 h-6 text-orange-600" />
                                    </div>
                                    <div className="flex gap-1">
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => openEditDialog(project)}
                                            className="h-8 w-8 text-zinc-500 hover:text-zinc-900"
                                            data-testid={`edit-project-${project.id}`}
                                        >
                                            <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button 
                                            variant="ghost" 
                                            size="icon" 
                                            onClick={() => openDeleteDialog(project)}
                                            className="h-8 w-8 text-zinc-500 hover:text-red-600"
                                            data-testid={`delete-project-${project.id}`}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    </div>
                                </div>
                                <h3 className="text-xl font-bold text-zinc-900 mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                    {project.name}
                                </h3>
                                <div className="flex items-center gap-2 text-zinc-500">
                                    <Hash className="w-4 h-4" />
                                    <span className="font-mono text-sm">{project.billing_number}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="text-2xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>
                            {selectedProject ? 'Edit Project' : 'Create Project'}
                        </DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleSubmit} className="space-y-6" data-testid="project-form">
                        <div className="form-group">
                            <Label htmlFor="name" className="form-label">Project Name</Label>
                            <Input
                                id="name"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                placeholder="e.g., Website Redesign"
                                required
                                className="swiss-input"
                                data-testid="project-name-input"
                            />
                        </div>
                        <div className="form-group">
                            <Label htmlFor="billing_number" className="form-label">Billing Number</Label>
                            <Input
                                id="billing_number"
                                value={formData.billing_number}
                                onChange={(e) => setFormData({ ...formData, billing_number: e.target.value })}
                                placeholder="e.g., PRJ-2024-001"
                                required
                                className="swiss-input"
                                data-testid="project-billing-input"
                            />
                        </div>
                        <DialogFooter>
                            <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                                Cancel
                            </Button>
                            <Button type="submit" className="swiss-btn-primary" disabled={saving} data-testid="project-submit-btn">
                                {saving ? 'Saving...' : selectedProject ? 'Update' : 'Create'}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation */}
            <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete Project?</AlertDialogTitle>
                        <AlertDialogDescription>
                            This will permanently delete "{selectedProject?.name}" and all associated tasks and time entries. This action cannot be undone.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Cancel</AlertDialogCancel>
                        <AlertDialogAction 
                            onClick={handleDelete}
                            className="bg-red-600 hover:bg-red-700"
                            data-testid="confirm-delete-project"
                        >
                            Delete
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
